/**
 * Product compare page controller — /product-compare（方向 B：單一產品身分系統）
 *
 * 架構：整張比較表單一 state（products 陣列）為真相來源。產品身分（縮圖 + 品名 + 廠商 +
 * 移除鈕）內建於表格 thead。
 *
 * thead 釘頂機制（方向 B：垂直 pin = JS translateY，水平 = 原生）：
 *   wrapper 要 overflow-x:auto 讓窄螢幕橫拉，但 CSS 規範下「overflow 任一軸非 visible → 另一軸
 *   computed 由 visible 變 auto」，wrapper 因此同時是垂直捲動容器卻無垂直溢出，thead 原生
 *   position:sticky(top) 會相對 wrapper 不相對 window → page 捲動永不 pin。改用 rAF 節流的
 *   window scroll 監聽，對 thead 施 transform:translateY 把它釘在 nav 下緣（表頂捲到 nav 下時
 *   施力、捲過表底時放開）。thead 留在表格內 → 欄寬/橫向 cell 對齊全自動原生跟手，零 translateX
 *   同步（仍保有方向 B 紅利）。corner 在 thead 內：垂直隨 transform 一起 pin、水平自身 sticky:left
 *   原生黏左（實測 transform 不破壞子元素 sticky-left），雙軸黏成立。
 * 移除了舊的頂部大卡（buildCards）與浮出縮圖列（buildStickyId + IntersectionObserver 哨兵 +
 * translateX 橫向手動同步）。
 *
 * 設計：
 *   - 事件委派：只在容器掛一個 click listener，重渲染不累積 listener（避免洩漏）。
 *   - 批次 DOM：先組字串再單次 innerHTML，不在迴圈內 append。
 *   - 最少保留 2 個產品才有比較意義；剩 2 個時移除鈕停用並提示。
 *   - 純前端 prototype，無持久化；用 data-remove-compare（非全域 compare-bar 的 [data-add-compare]
 *     慣例），避免誤觸 compare-bar.js 的全域監聽。
 *   - 空狀態文案二分（bug fix）：選取 <2 項 vs 選取 ≥2 項但目前對映不到比較資料（COMPARE_PRODUCTS
 *     只 3 筆 demo 白名單，其餘品項尚未提供比較規格）。後者若沿用「至少需要兩項產品」文案會誤導
 *     使用者（明明選了 ≥2 項）。詳見 readCompareSelection / applyEmptyStateCopy。
 */

import { COMPARE_PRODUCTS, COMPARE_SPEC_ROWS } from '../data/product-compare-items.js';
// STORAGE_KEY 從 compare-bar.js import（單一真相源）：比較表依使用者實際選取（compare-bar 寫入
// 的 [{id,...}] 物件陣列）渲染對應欄，不再寫死全部產品。兩處共用同一 key 常數 → 不漂移。
import { STORAGE_KEY as COMPARE_STORAGE_KEY } from '../components/compare-bar.js';
// cell 值渲染器抽到共用模組（與 product-detail-specs.js 共用同一份 → bool/badge/verified 視覺一致）。
// esc 也在此（buildColHead 富欄頭與 cell 渲染共用同一 escape）。
import { renderCellValue, esc } from '../components/compare-cell.js';

const SELECTORS = {
  root: '[data-compare-root]',
  table: '[data-compare-table]',
  empty: '[data-compare-empty]',
  count: '[data-compare-count]',
  announce: '[data-compare-announce]',
};

const MIN_PRODUCTS = 2;

// 比較上限：與 compare-bar.js MAX_SLOTS 對齊（被竄改 storage 超量時 slice 到此值）。
const MAX_PRODUCTS = 3;

// 角落欄（規格名稱列）固定寬（%）。產品欄均分剩餘 = (100 - 此值)/N 撐滿 100%。
// 同值由 buildTable 寫上 --compare-corner-width inline，CSS .compare-table__corner /
// .compare-table__row-head 讀同一值 → JS 公式與 CSS 寬零漂移（見 product-compare.css）。
const COMPARE_CORNER_PCT = 22;

/* ===== 比較選取 storage（與 compare-bar.js 共用同一 key；parse/read/write 集中此處不重複） ===== */

// 讀回 compare-bar 寫入的物件陣列（[{id,title,img,category}]）；壞 JSON / 非陣列 / 無值 → []。
// readCompareSelection 與移除同步（onClick）共用此 parse，避免兩處各寫 try/catch 漂移。
function parseStoredItems() {
  try {
    const raw = localStorage.getItem(COMPARE_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

// 把選取（storage 中的 id 序）映射成本頁產品物件：取每筆 .id → slice 到上限 → 對映 COMPARE_PRODUCTS
// （依 id 找；找不到的 id 丟棄）→ filter(Boolean)。回傳 { products, rawSelectionCount }：
//   products          — storage 順序的產品清單（= 比較表欄序）。
//   rawSelectionCount — 使用者原始選取筆數（parseStoredItems().length，不經白名單過濾）。用途：
//     區分「使用者只選了 0/1 項」vs「選了 ≥2 項但目前都/部分對映不到比較資料」——後者若只看
//     products.length 會誤觸「至少需要兩項產品」空狀態文案（使用者明明選了 ≥2 項）。
//     見 render() 呼叫 applyEmptyStateCopy 的分支。
function readCompareSelection() {
  const stored = parseStoredItems();
  const products = stored
    .map((it) => it && it.id)
    .slice(0, MAX_PRODUCTS)
    .map((id) => COMPARE_PRODUCTS.find((p) => p.id === id))
    .filter(Boolean);
  return { products, rawSelectionCount: stored.length };
}

// 移除某 id 後寫回 storage（parse → 濾掉該 id → stringify → set）。寫入失敗靜默（同 compare-bar）。
// 目的：頁內移除一欄後 reload 不會因 storage 殘留把它帶回來（修 state/storage 漂移）。
function removeFromCompareStorage(id) {
  try {
    const next = parseStoredItems().filter((it) => it && it.id !== id);
    localStorage.setItem(COMPARE_STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

// thead 釘頂偏移：thead 被 pin 時其頂緣應落在 nav 下緣，不蓋住品名。量測值（頂部固定堆疊底緣）
// 寫入此 CSS 變數（inline style 寫到 .compare-table）作為 pin 目標 Y，pinThead() 讀它算 translateY。
// 之前寫死 60（僅 logo box 高），漏算 nav 上下 padding → 桌機實際 nav 帶高 112px。改為動態量測
// 真正貼頂的固定元素底緣，跨斷點（手機 nav 較矮 ~84）皆正確。
const THEAD_TOP_VAR = '--compare-thead-top';

// 量測「會固定貼在視窗頂端的元素」釘頂後合計高度（＝thead 該讓出的頂部偏移，px）。
// 與「目前捲動位置」無關：只認 position:fixed/sticky 且錨定頂端（top 計算值 ≈0）者，累加其 rect.height。
//   - sticky top:0 的 nav：釘頂後 bottom＝自身高度，故用 height 累加，不依賴「現在是否已貼頂」
//     （避免在 scroll≠0 或上方有 relative 元素把 nav 推離 top 時量測失準）。
//   - 本頁實況：頂部僅 1 層 sticky（.site-nav，內頁 top:0），無 render 公告列（.top-bar 為
//     position:relative，會隨文件流捲走、不屬固定堆疊，下方 computed-position 檢查會自動排除）。
//   - .top-bar 一併列入候選僅為「未來該頁改成 sticky 公告列」的前瞻；現況被 position 檢查濾掉。
// 回傳 0 表示頂部無固定堆疊（保底讓 thead 退回貼頂，不誤蓋）。
function measureStickyStackBottom() {
  const candidates = document.querySelectorAll('.top-bar, .site-nav');
  let total = 0;
  candidates.forEach((el) => {
    if (el.classList.contains('top-bar--dismissed')) return;
    const cs = getComputedStyle(el);
    if (cs.position !== 'fixed' && cs.position !== 'sticky') return;
    // 錨定頂端才算頂部堆疊（top 為具體值且接近 0）；bottom-anchored / auto 不計入。
    const topVal = parseFloat(cs.top);
    if (!Number.isFinite(topVal) || topVal > 1) return;
    total += el.getBoundingClientRect().height;
  });
  return Math.round(total);
}

const REMOVE_ICON =
  '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
  '<path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';

// 縮圖載入失敗時的中性佔位（自包含 inline SVG，比照 _icon-avatar-default.svg.html /
// clinic-logo 預設 icon 慣例）：在既有 --color-bg-surface 方框內畫中性「圖片」glyph，
// 顏色走 --color-ink-tertiary（token only，無 hardcode）。預設 hidden，img onerror 時顯示。
const THUMB_FALLBACK_ICON =
  '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">' +
  '<rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" stroke-width="1.6"/>' +
  '<circle cx="8.5" cy="9.5" r="1.6" fill="currentColor"/>' +
  '<path d="M5 18l4.5-5 3 3.5L16 12l3 6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>' +
  '</svg>';

/**
 * 產出單一產品的富欄頭 th（字串）。垂直堆疊：縮圖連結 → 品名（最高層級）→ 廠商（副標）。
 * 移除鈕 absolute 定位於右上角；tagline（highlight）不渲染（資料保留、避免撐高 thead）。
 * 縮圖小（48px）不掛 loading=lazy（無 CLS 顧慮）→ 避開 main.css 的 img[loading=lazy]{opacity:0}
 * gate 對 innerHTML 重建新圖永久隱形的問題（方案 a）。
 * 縮圖 src 用 import.meta.env.BASE_URL 拼接（base-aware，子路徑部署安全；與 icon.js:34 /
 * hero-icon-3d.js:31 同慣例）—— innerHTML 字串 Vite build 不會改寫，裸 './images' 在子路徑下會錯。
 * 圖真的缺漏時：img onerror（由 attachThumbFallback 綁定）隱藏 img、顯中性佔位，不露瀏覽器破圖 icon。
 */
function buildColHead(p, canRemove) {
  const thumbSrc = `${import.meta.env.BASE_URL}images/categories/${esc(p.img)}`;
  // href 同 thumbSrc 慣例：data 存「頁面檔名」（'product-detail.html'），用 BASE_URL 拼成
  // base-aware 連結（base:'./' → './product-detail.html'）。裸 '/product-detail' root-絕對
  // 在子路徑（.../dist/）會掉前綴打網域根 → 404；Vite 不改寫 JS innerHTML 字串、postprocess
  // 也只改靜態 HTML <a href>。本頁 product-compare 為 root 頁，相對連結 dev/build 都解析正確。
  const productHref = `${import.meta.env.BASE_URL}${esc(p.href)}`;
  return `<th scope="col" class="compare-table__col-head">
      <button
        class="compare-table__col-remove"
        type="button"
        data-remove-compare="${esc(p.id)}"
        aria-label="從比較中移除 ${esc(p.name)}"
        ${canRemove ? '' : 'disabled aria-disabled="true"'}
      >${REMOVE_ICON}</button>
      <a class="compare-table__col-link" href="${productHref}" aria-label="${esc(p.name)}（前往產品頁）">
        <img class="compare-table__col-thumb" src="${thumbSrc}" alt="${esc(p.alt)}" width="48" height="48" data-thumb-fallback />
        <span class="compare-table__col-thumb-fallback" aria-hidden="true" hidden>${THUMB_FALLBACK_ICON}</span>
      </a>
      <span class="compare-table__col-name">${esc(p.name)}</span>
      <span class="compare-table__col-vendor">${esc(p.vendor)}</span>
    </th>`;
}

/**
 * 為比較表所有縮圖綁 onerror 佔位 fallback。innerHTML 重建後呼叫：對每張 [data-thumb-fallback]
 * 圖綁 error → 隱藏圖、顯示同框中性佔位。並補捉「綁定前已失敗（cache 命中的 404）」的情況
 * （complete && naturalWidth===0）→ 立即觸發，避免錯過不再 fire 的 error 事件。
 */
function attachThumbFallback(els) {
  const imgs = els.table.querySelectorAll('img[data-thumb-fallback]');
  imgs.forEach((img) => {
    const showFallback = () => {
      img.hidden = true;
      const ph = img.parentElement?.querySelector('.compare-table__col-thumb-fallback');
      if (ph) ph.hidden = false;
    };
    img.addEventListener('error', showFallback, { once: true });
    if (img.complete && img.naturalWidth === 0) showFallback();
  });
}

/** 產出「比較表」HTML（字串）。thead 為富欄頭（產品身分），tbody 為規格列。 */
function buildTable(products) {
  // 角落欄（規格名稱列）為固定寬，產品欄均分剩餘寬度撐滿 100%。
  // CORNER 與 CSS 的 .compare-table__corner/.compare-table__row-head width 同源：把同值寫上
  // --compare-corner-width inline，CSS 讀同一值 → 公式 (100-CORNER)/N 與 CSS 零漂移。
  // 修正前 100/(N+1) 漏算固定角落欄：N=2 → 22 + 2×33.3 = 88.7% 留 11% 缺口（誤像「3 欄少 1」）。
  // 修正後 N=2 → 39%×2、N=3 → 26%×3，加角落 22% 皆滿 100%。
  const colWidth = `${((100 - COMPARE_CORNER_PCT) / products.length).toFixed(4)}%`;
  const canRemove = products.length > MIN_PRODUCTS;

  const headCells = products.map((p) => buildColHead(p, canRemove)).join('');

  const bodyRows = COMPARE_SPEC_ROWS.map((row) => {
    const cells = products
      .map(
        (p) =>
          `<td class="compare-table__cell">${renderCellValue(row, p.specs[row.key])}</td>`
      )
      .join('');
    return `<tr class="compare-table__row">
        <th scope="row" class="compare-table__row-head">${esc(row.label)}</th>
        ${cells}
      </tr>`;
  }).join('');

  return `
    <table class="compare-table" style="--compare-col-width: ${colWidth}; --compare-corner-width: ${COMPARE_CORNER_PCT}%;">
      <caption class="visually-hidden">產品規格逐項比較表</caption>
      <thead>
        <tr>
          <th scope="col" class="compare-table__corner">比較項目</th>
          ${headCells}
        </tr>
      </thead>
      <tbody>
        ${bodyRows}
      </tbody>
    </table>`;
}

// 右緣可捲提示（議題 1）：wrapper 還有右側內容沒看到 → 在外框 .compare-table-frame 掛 [data-can-scroll-right]
// （CSS 顯示右緣漸層 ::after）。捲到底 / 桌機不溢出（scrollWidth==clientWidth）/ 容器隱藏（產品<2）→ 移除屬性、漸層消。
function updateScrollHint(els) {
  const wrap = els.table.parentElement; // .compare-table-wrapper（橫向捲動容器）
  const frame = wrap && wrap.parentElement; // .compare-table-frame（非捲動外框，托 ::after）
  if (!wrap || !frame) return;
  const canRight = wrap.scrollWidth - wrap.clientWidth - wrap.scrollLeft > 1;
  frame.toggleAttribute('data-can-scroll-right', canRight);
}

// 空狀態兩種情境共用同一個 .compare-empty 容器與既有 class（不新增樣式/token，只切文字）：
//   isAmbiguous=false（預設）— 選取 0/1 項，需要使用者再選；沿用既有文案。
//   isAmbiguous=true        — 選取 ≥2 項，但目前都/部分對映不到比較資料（見 readCompareSelection）；
//                             改一句誠實訊息，不再暗示「你選不到兩項」（他其實選夠了）。
// 工程預設文案（無 uiux/content 規格）：措辭沿用既有句型與語氣，建議後續交 content/uiux 定稿。
const EMPTY_STATE_COPY = {
  default: {
    title: '至少需要兩項產品才能比較',
    desc: '請回到產品資料庫，挑選你想對照的產品再加入比較。',
  },
  unavailable: {
    title: '所選產品尚未提供比較資料',
    desc: '你選取的產品目前不在比較範圍內，請回到產品資料庫，選擇其他可比較的產品。',
  },
};

function applyEmptyStateCopy(els, isAmbiguous) {
  const copy = isAmbiguous ? EMPTY_STATE_COPY.unavailable : EMPTY_STATE_COPY.default;
  if (els.emptyTitle) els.emptyTitle.textContent = copy.title;
  if (els.emptyDesc) els.emptyDesc.textContent = copy.desc;
}

function render(state, els) {
  const { products, rawSelectionCount } = state;

  if (els.count) {
    els.count.textContent = String(products.length);
  }

  const hasEnough = products.length >= MIN_PRODUCTS;
  els.empty.hidden = hasEnough;
  els.table.parentElement.hidden = !hasEnough;

  if (!hasEnough) {
    els.table.innerHTML = '';
    updateScrollHint(els); // 切回空態時清掉可能殘留的 fade 屬性
    // 選了 ≥2 項但對映不到比較資料 → 換誠實文案；真正選 0/1 項 → 維持既有「至少需要兩項產品」文案。
    applyEmptyStateCopy(els, rawSelectionCount >= MIN_PRODUCTS);
    return;
  }

  els.table.innerHTML = buildTable(products);
  // 縮圖 onerror 佔位 fallback 須在 innerHTML 重建後重綁（舊 listener 隨舊節點銷毀）。
  attachThumbFallback(els);
  // 重渲染後重新量測 thead 頂部偏移寫入新表（表是 innerHTML 重建的，舊 inline 變數不在新節點上），
  // 並立即依當前捲動位置重算 pin（避免重渲染瞬間 thead transform 殘留或漏 pin）。
  applyTheadTop(els);
  pinThead(els);
  updateScrollHint(els); // 表注入後評估是否需右緣漸層（議題 1）
}

// 量測頂部固定堆疊底緣 → 寫入 .compare-table 的 --compare-thead-top（pinThead 讀它當 pin 目標 Y）。
// 表不存在（產品不足 / 尚未渲染）時靜默跳過。
function applyTheadTop(els) {
  const table = els.table.querySelector('.compare-table');
  if (!table) return;
  const stackBottom = measureStickyStackBottom();
  if (stackBottom > 0) {
    table.style.setProperty(THEAD_TOP_VAR, `${stackBottom}px`);
  }
}

// 垂直 pin：依當前捲動位置對 thead 施 transform:translateY，把它釘在 nav 下緣。
//   pinTop     = 頂部固定堆疊底緣（--compare-thead-top；nav 下緣，pin 後 thead 頂緣落在此）
//   tableTop   = 表格 border-box 頂緣相對 viewport。關鍵：transform 施在「子元素 thead」上，
//                不改變父 table 的 layout box，故 table.rect.top 永遠是「未受 pin 影響」的自然位置
//                （已實測：對 thead translateY(50) 後 table.rect.top 不動）→ 不需回推 currentDy。
//   dy         = clamp(0, pinTop - tableTop, tableHeight - theadHeight)
//     - 表頂尚在 pinTop 下方（未捲到 nav）→ pinTop - tableTop < 0 → clamp 0（thead 歸位、隨表捲）
//     - 表頂捲過 nav → dy>0 把 thead 往下推回 nav 下緣（視覺釘住）
//     - 上限 tableHeight - theadHeight：thead 不超過表底（捲過整表後欄頭跟著離場，不懸空蓋住 footer）
function pinThead(els) {
  const table = els.table.querySelector('.compare-table');
  if (!table) return;
  const thead = table.tHead;
  if (!thead) return;

  const pinTop = parseFloat(getComputedStyle(table).getPropertyValue(THEAD_TOP_VAR)) || 0;
  const tableRect = table.getBoundingClientRect();
  const theadHeight = thead.getBoundingClientRect().height;
  const maxDy = Math.max(0, tableRect.height - theadHeight);

  const dy = Math.max(0, Math.min(pinTop - tableRect.top, maxDy));
  const rounded = Math.round(dy);

  const currentDy = Math.round(parseFloat(thead.dataset.pinDy || '0') || 0);
  if (rounded === currentDy) return; // 無變化不寫 DOM（省重繪）
  thead.dataset.pinDy = String(rounded);
  thead.style.transform = rounded > 0 ? `translateY(${rounded}px)` : '';
}

function init() {
  const root = document.querySelector(SELECTORS.root);
  if (!root) return null;

  const emptyEl = root.querySelector(SELECTORS.empty);
  const els = {
    table: root.querySelector(SELECTORS.table),
    empty: emptyEl,
    // 空狀態兩句文案節點（見 applyEmptyStateCopy）；emptyEl 缺席時保底 null，非關鍵節點不擋 init。
    emptyTitle: emptyEl?.querySelector('.compare-empty__title') ?? null,
    emptyDesc: emptyEl?.querySelector('.compare-empty__desc') ?? null,
    count: document.querySelector(SELECTORS.count),
    announce: document.querySelector(SELECTORS.announce),
  };
  if (!els.table || !els.empty) return null;

  // state：依使用者實際選取（compare-bar 寫入的 localStorage）渲染對應欄，不再寫死全部產品。
  // readCompareSelection 已對映到 COMPARE_PRODUCTS 物件 → 複製一份避免改到 module 匯出常數。
  // 選取 < 2（含 0/1）→ render() 的 MIN_PRODUCTS gate 接手顯示既有空狀態（不在此另建）。
  // rawSelectionCount 只在 init 量一次：後續唯一會改動 state.products 的路徑是移除鈕 onClick，
  // 該鈕僅存在於「已渲染出表格」（hasEnough）情境，移除動作本身同步 storage/state，不會重新落入
  // 「選了但對映不到」的曖昧態，故不需在 onClick 內重算。
  const selection = readCompareSelection();
  const state = {
    products: selection.products.map((p) => ({ ...p })),
    rawSelectionCount: selection.rawSelectionCount,
  };

  function onClick(event) {
    const trigger = event.target.closest('[data-remove-compare]');
    if (!trigger || trigger.disabled) return;
    const id = trigger.getAttribute('data-remove-compare');
    if (state.products.length <= MIN_PRODUCTS) return;

    const removed = state.products.find((p) => p.id === id);

    // 移除前先決定移除後的焦點落點（DOM 即將被 innerHTML 重建，先記 id 再重建後重抓）：
    // 取目前所有移除鈕的 id 序，挑被移除者「之後」的那顆；無則取「之前」那顆。重建後若該欄仍在
    // 且可移除 → focus 它；若移除後降到下限（移除鈕全 disabled）→ focus 表角落，避免焦點掉到 <body>。
    const removeIds = Array.from(
      els.table.querySelectorAll('[data-remove-compare]')
    ).map((b) => b.getAttribute('data-remove-compare'));
    const idx = removeIds.indexOf(id);
    const neighborId =
      removeIds[idx + 1] ?? removeIds[idx - 1] ?? null;

    state.products = state.products.filter((p) => p.id !== id);
    // 同步 storage：把該 id 從 compare-bar:items 移除，否則移除後 reload 會被 storage 殘留帶回。
    removeFromCompareStorage(id);
    render(state, els);

    // 焦點管理：剩餘仍高於下限 → 移到鄰欄移除鈕；已達下限（無可移除欄）→ 移到表角落。
    const stillRemovable = state.products.length > MIN_PRODUCTS;
    let focusTarget = null;
    if (stillRemovable && neighborId != null) {
      focusTarget = els.table.querySelector(
        `[data-remove-compare="${CSS.escape(neighborId)}"]`
      );
    }
    if (!focusTarget) {
      focusTarget = els.table.querySelector('.compare-table__corner');
      // corner 是 <th>，預設不可聚焦 → 暫設 tabindex=-1 才能接住程式化 focus（不進 Tab 序）。
      if (focusTarget && !focusTarget.hasAttribute('tabindex')) {
        focusTarget.setAttribute('tabindex', '-1');
      }
    }
    if (focusTarget) focusTarget.focus();

    // aria-live 朗讀移除結果（區塊在 product-compare.html，視覺隱藏 role=status）。
    if (els.announce && removed) {
      els.announce.textContent = `已移除 ${removed.name}，目前比較 ${state.products.length} 項`;
    }
  }

  // 單一委派 listener 掛在 root；重渲染（innerHTML 換掉子樹）不影響此 listener
  root.addEventListener('click', onClick);

  // resize 時 nav 高度可能跨斷點變化（手機較矮），重量 thead top 變數並重算 pin。rAF 合併多次 event。
  let resizeRaf = 0;
  function onResize() {
    if (resizeRaf) return;
    resizeRaf = requestAnimationFrame(() => {
      resizeRaf = 0;
      applyTheadTop(els);
      pinThead(els);
      updateScrollHint(els); // 斷點變化可能改變是否溢出（桌機↔手機）→ 重評右緣漸層
    });
  }
  window.addEventListener('resize', onResize, { passive: true });

  // 垂直捲動時把 thead 釘在 nav 下緣（rAF 節流：每幀最多一次量測 + 寫 transform，避免 scroll 抖動）。
  let scrollRaf = 0;
  function onScroll() {
    if (scrollRaf) return;
    scrollRaf = requestAnimationFrame(() => {
      scrollRaf = 0;
      pinThead(els);
    });
  }
  window.addEventListener('scroll', onScroll, { passive: true });

  // 比較表橫向捲動時更新右緣漸層提示（議題 1）。監聽橫向捲動容器 .compare-table-wrapper（非 window），
  // rAF 節流。wrapper 為靜態 HTML（僅內層 [data-compare-table] 會 innerHTML 重建）→ 綁一次即可。
  const scrollWrap = els.table.parentElement;
  let hintRaf = 0;
  function onWrapScroll() {
    if (hintRaf) return;
    hintRaf = requestAnimationFrame(() => {
      hintRaf = 0;
      updateScrollHint(els);
    });
  }
  if (scrollWrap) scrollWrap.addEventListener('scroll', onWrapScroll, { passive: true });

  function destroy() {
    root.removeEventListener('click', onClick);
    window.removeEventListener('resize', onResize);
    window.removeEventListener('scroll', onScroll);
    if (scrollWrap) scrollWrap.removeEventListener('scroll', onWrapScroll);
    if (resizeRaf) cancelAnimationFrame(resizeRaf);
    if (scrollRaf) cancelAnimationFrame(scrollRaf);
    if (hintRaf) cancelAnimationFrame(hintRaf);
  }
  // 離開頁面時釋放（SPA 化或 bfcache 還原時的保險）
  window.addEventListener('pagehide', destroy, { once: true });

  render(state, els);
  return { destroy };
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
